import React, { useEffect, useState } from "react";
import api from "../lib/api";
import CommunityPostCard from "../components/CommunityPostCard";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, TrendingUp, Clock, Flame, Twitter, Target, Trophy } from "lucide-react";

const ASSETS = ["BTC", "ETH", "SOL", "XRP", "BNB"];

function Leaderboard() {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/community/leaderboard")
      .then((r) => setLeaders(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (leaders.length === 0) return null;

  return (
    <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-4 h-4 text-yellow-400" />
        <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)]">Top Analysts</h3>
      </div>
      <div className="space-y-3">
        {leaders.slice(0, 5).map((l, i) => (
          <div key={l.author_id} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-[var(--text-tertiary)] w-4">{i + 1}</span>
              <div className="w-6 h-6 bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
                <span className="text-yellow-400 font-bold text-xs">{l.author_name[0]}</span>
              </div>
              <span className="text-sm text-[var(--text-secondary)] truncate max-w-[80px]">{l.author_name}</span>
            </div>
            <div className="text-right">
              <div className="text-xs font-mono font-bold text-yellow-400">{l.accuracy}%</div>
              <div className="text-xs font-mono text-[var(--text-tertiary)]">{l.correct}/{l.total}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Community() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("new");
  const [postType, setPostType] = useState("discussion");
  const [asset, setAsset] = useState("BTC");
  const [targetPrice, setTargetPrice] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [direction, setDirection] = useState("above");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/community/posts?limit=50");
      setPosts(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [user?.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    if (postType === "prediction" && (!targetPrice || !targetDate)) {
      return toast.error("Please fill in all prediction fields");
    }
    setPosting(true);
    try {
      if (postType === "prediction") {
        await api.post("/community/predictions", {
          title, content,
          asset, target_price: parseFloat(targetPrice),
          target_date: targetDate, direction
        });
      } else {
        await api.post("/community/posts", { title, content });
      }
      setTitle(""); setContent(""); setTargetPrice(""); setTargetDate("");
      toast.success("Posted to the feed");
      load();
    } catch (err) {
      toast.error("Could not publish post");
    } finally { setPosting(false); }
  };

  const sorted = [...posts].sort((a, b) => {
    if (b.pinned !== a.pinned) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
    if (sort === "top") return (b.likes_count || 0) - (a.likes_count || 0);
    if (sort === "hot") return ((b.likes_count || 0) + (b.comments_count || 0)) - ((a.likes_count || 0) + (a.comments_count || 0));
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const shareToTwitter = (post) => {
    const text = encodeURIComponent(post.title + " — MediaCrypto Community");
    const url = encodeURIComponent("https://mediacrypto.ai/community/" + post.id);
    window.open("https://twitter.com/intent/tweet?text=" + text + "&url=" + url, "_blank", "noopener,noreferrer");
  };

  const postCount = posts.length;

  return (
    <div className="pt-10 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-4 grid grid-cols-1 lg:grid-cols-12 gap-8">

        <div className="lg:col-span-8 space-y-5">

          {user ? (
            <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6">
              {/* Post type selector */}
              <div className="flex items-center gap-2 mb-5">
                <button
                  onClick={() => setPostType("discussion")}
                  className={"flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition " +
                    (postType === "discussion" ? "bg-yellow-400 text-black border-yellow-400" : "border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400")}
                >
                  <Send className="w-3 h-3" /> Discussion
                </button>
                <button
                  onClick={() => setPostType("prediction")}
                  className={"flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition " +
                    (postType === "prediction" ? "bg-yellow-400 text-black border-yellow-400" : "border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400")}
                >
                  <Target className="w-3 h-3" /> Price Prediction
                </button>
              </div>

              <form onSubmit={submit}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 flex-shrink-0 rounded-full overflow-hidden flex items-center justify-center">
  {user.avatar_url ? (
    <img src={user.avatar_url} alt={user.name} className="w-9 h-9 object-cover" />
  ) : (
    <div className="w-9 h-9 bg-yellow-400 text-black font-bold flex items-center justify-center text-sm">
      {(user.name || user.email)[0].toUpperCase()}
    </div>
  )}
</div>
                  <span className="text-sm text-[var(--text-tertiary)] font-mono">Posting as <span className="text-[var(--text-secondary)]">{user.name}</span></span>
                </div>

                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={postType === "prediction" ? "e.g. BTC will hit $80K by July 31" : "Post title..."}
                  className="w-full bg-transparent border-b border-[var(--border-soft)] px-0 py-2 text-lg font-display font-bold focus:outline-none focus:border-yellow-400 text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                />

                {postType === "prediction" && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Asset</label>
                      <select value={asset} onChange={(e) => setAsset(e.target.value)}
                        className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
                        {ASSETS.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Direction</label>
                      <select value={direction} onChange={(e) => setDirection(e.target.value)}
                        className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
                        <option value="above">Above</option>
                        <option value="below">Below</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Target Price $</label>
                      <input type="number" value={targetPrice} onChange={(e) => setTargetPrice(e.target.value)}
                        placeholder="e.g. 80000"
                        className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 placeholder:text-[var(--text-tertiary)]" />
                    </div>
                    <div>
                      <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Target Date</label>
                      <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)}
                        className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
                    </div>
                  </div>
                )}

                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={postType === "prediction" ? "Share your analysis and reasoning..." : "Share your alpha, charts, or hot takes..."}
                  rows={4}
                  className="w-full mt-4 bg-transparent text-[var(--text-secondary)] placeholder:text-[var(--text-tertiary)] focus:outline-none resize-none"
                />
                <div className="flex justify-end mt-3 pt-3 border-t border-[var(--border-soft)]">
                  <button type="submit" disabled={posting}
                    className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-6 py-2.5 hover:bg-yellow-500 transition disabled:opacity-50 flex items-center gap-2 text-sm">
                    {postType === "prediction" ? <Target className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                    {postType === "prediction" ? "Post Prediction" : "Publish Post"}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-8 text-center">
              <p className="text-[var(--text-secondary)] mb-4 text-lg">Sign in to publish posts and join the conversation.</p>
              <div className="flex justify-center gap-3">
                <Link to="/login" className="border border-[var(--border-soft)] hover:border-yellow-400 hover:text-yellow-400 px-6 py-2 text-sm uppercase tracking-wide transition text-[var(--text-primary)]">Sign in</Link>
                <Link to="/register" className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-6 py-2 hover:bg-yellow-500 transition">Join free</Link>
              </div>
            </div>
          )}

          {/* Sort bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              {[
                { id: "new", label: "New", icon: Clock },
                { id: "top", label: "Top", icon: TrendingUp },
                { id: "hot", label: "Hot", icon: Flame },
              ].map(({ id, label, icon: Icon }) => (
                <button key={id} onClick={() => setSort(id)}
                  className={"flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition " +
                    (sort === id ? "bg-yellow-400 text-black border-yellow-400" : "border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400")}>
                  <Icon className="w-3 h-3" /> {label}
                </button>
              ))}
            </div>
            <span className="text-xs font-mono text-[var(--text-tertiary)]">{postCount} posts</span>
          </div>

          {/* Feed */}
          <div className="space-y-4">
            {loading ? (
              <div className="text-[var(--text-tertiary)] font-mono text-sm">Loading feed...</div>
            ) : sorted.length === 0 ? (
              <div className="text-[var(--text-tertiary)] p-8 border border-[var(--border-soft)] bg-[var(--bg-panel)] text-center">
                <p className="text-lg font-display font-bold text-[var(--text-primary)] mb-2">No posts yet.</p>
                <p className="text-sm">Be the first to start the conversation.</p>
              </div>
            ) : (
              sorted.map((p) => (
                <div key={p.id} className="relative">
                  {p.pinned && (
                    <div className="flex items-center gap-1.5 px-4 pt-3 pb-0">
                      <span className="text-xs font-mono text-yellow-400 uppercase tracking-widest">📌 Pinned</span>
                    </div>
                  )}
                  <div className={p.pinned ? "border border-yellow-400/30 bg-[var(--bg-panel)]" : ""}>
                    <CommunityPostCard post={p} onChanged={load} />
                    <div className="px-4 pb-3 flex justify-end">
                      <button onClick={() => shareToTwitter(p)}
                        className="flex items-center gap-1.5 text-xs font-mono text-[var(--text-tertiary)] hover:text-yellow-400 transition">
                        <Twitter className="w-3 h-3" /> Share
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-5">
          {!user && (
            <div className="bg-yellow-400/5 border border-yellow-400/20 p-6">
              <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">Join the conversation</h3>
              <p className="text-sm text-[var(--text-secondary)] mb-4">Post your analysis, share alpha, and connect with crypto traders.</p>
              <Link to="/register" className="block text-center bg-yellow-400 text-black font-bold uppercase tracking-wide px-4 py-2.5 hover:bg-yellow-500 transition text-sm">
                Create Free Account
              </Link>
              <Link to="/login" className="block text-center mt-2 text-xs font-mono text-[var(--text-tertiary)] hover:text-yellow-400 transition">
                Already have an account? Sign in
              </Link>
            </div>
          )}

          <Leaderboard />

          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)] mb-4">Community Stats</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--text-secondary)]">Total posts</span>
                <span className="font-mono font-bold text-[var(--text-primary)]">{postCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--text-secondary)]">This week</span>
                <span className="font-mono font-bold text-yellow-400">
                  {posts.filter(p => new Date(p.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)] mb-4">Community Guidelines</h3>
            <ul className="space-y-3 text-sm text-[var(--text-secondary)]">
              <li className="flex gap-2"><span className="text-yellow-400">+</span> High-signal posts only.</li>
              <li className="flex gap-2"><span className="text-yellow-400">+</span> No financial advice.</li>
              <li className="flex gap-2"><span className="text-yellow-400">+</span> Cite sources for data.</li>
              <li className="flex gap-2"><span className="text-yellow-400">+</span> Disagree with ideas, not people.</li>
              <li className="flex gap-2"><span className="text-yellow-400">+</span> Predictions are tracked publicly.</li>
            </ul>
          </div>

          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)] mb-4">Trending Topics</h3>
            <div className="flex flex-wrap gap-2">
              {["#BTC", "#ETH", "#Solana", "#CLARITY", "#ETF", "#DeFi", "#Macro", "#HYPE"].map((t) => (
                <span key={t} className="px-3 py-1 border border-[var(--border-soft)] text-xs font-mono text-[var(--text-secondary)] hover:text-yellow-400 hover:border-yellow-400/40 cursor-pointer transition">{t}</span>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}